// src/utils/tokenManager.js
import { auth } from '../config/firebaseConfig';

// Function to clear auth cookie
export async function clearAuthCookie() {
  try {
    await fetch('/api/auth/clear-token', {
      method: 'POST',
      credentials: 'include'
    });
    return true;
  } catch (error) {
    console.error('Error clearing auth cookie:', error);
    return false;
  }
}

// Get a valid token and refresh if needed
export async function getValidToken(force = false) {
  const currentUser = auth.currentUser;
  if (!currentUser) return null;
  
  try {
    return await currentUser.getIdToken(force);
  } catch (error) {
    console.error('Error getting Firebase token:', error);
    return null;
  }
}

// Set the token in cookie
export async function syncTokenWithCookie(token) {
  if (!token) return false;
  
  try {
    const response = await fetch('/api/auth/set-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
      credentials: 'include'
    });
    
    return response.ok;
  } catch (error) {
    console.error('Error syncing token with cookie:', error);
    return false;
  }
}

/**
 * Enhanced fetch with automatic token refresh
 * @param {string} url - URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
export async function fetchWithToken(url, options = {}) {
  // Get valid token
  const token = await getValidToken();
  
  if (!token && options.requireAuth !== false) {
    throw new Error('No valid authentication token available');
  }
  
  // Add token to headers if available
  const fetchOptions = {
    ...options,
    headers: {
      ...options.headers,
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    }
  };
  
  try {
    // First attempt
    const response = await fetch(url, fetchOptions);
    
    // Handle auth errors by refreshing token and retrying once
    if ((response.status === 401 || response.status === 403) && token) {
      console.log('Got 401/403, forcing token refresh and retrying...');
      
      // Force token refresh
      const newToken = await getValidToken(true);
      
      if (newToken) {
        // Also sync with cookie to ensure consistency
        await syncTokenWithCookie(newToken);
        
        // Retry with new token
        const retryOptions = {
          ...fetchOptions,
          headers: {
            ...fetchOptions.headers,
            'Authorization': `Bearer ${newToken}`
          }
        };
        
        return fetch(url, retryOptions);
      }
    }
    
    return response;
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}